/**
 * The bring-your-own custom-domain rules (kitecorp/transferify-api-service#348, extended by
 * kitecorp/devops#126 phase B), React-free by mandate: the editor and its hook only render what this
 * module decides.
 *
 * <p>This is a DIFFERENT feature from the claimed brand subdomain in `../subdomain`, despite sharing the
 * `customDomain` plan capability and the same custom-domain table. A claim is a label under one of our
 * zones and needs no proof; this is a host the sender owns, which needs a DNS challenge, a CNAME to our
 * edge and a managed certificate. The shared capability name is the trap kitecorp/transferify-ui#669 was
 * filed about — it is not evidence that this surface already existed.
 *
 * <p>Functions return a message KEY or a state, never copy — the catalog owns the words in both locales,
 * and a module that returned English would make the Romanian build silently wrong.
 */

/** The api's `@Size` bound on the request, which is the width of the `account_domains.domain` column. */
export const MAX_DOMAIN_LENGTH = 253;

/**
 * LDH hostname grammar, mirroring `CustomDomains.HOSTNAME_PATTERN` in the api.
 *
 * <p>The `{2,}` quantifier requires at least three labels, which is what rejects an apex (`acme.com`), a
 * bare TLD and a two-label public suffix (`co.uk`). That is deliberate on the server: v1 supports
 * subdomains only, because an apex needs an ALIAS record most registrars do not offer.
 *
 * <p>Deliberately NOT stricter than the server's, and deliberately does NOT restate its reserved-host list
 * (our own zones, `localhost`). A client rule rejecting something the server accepts would make a
 * legitimate domain unregisterable with no way to find out why; the reserved case arrives as the api's own
 * localized sentence instead, exactly as the sibling claim editor treats reserved labels.
 */
const HOSTNAME = /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?\.){2,}[A-Za-z]{2,63}$/;

export type DomainValidationError = "empty" | "tooLong" | "invalid";

export type DomainValidation =
	| {valid: true; domain: string}
	| {valid: false; error: DomainValidationError};

/**
 * Normalizes what the user typed into what the api stores: trimmed, lowercased, and stripped of the
 * optional trailing root dot. `files.ACME.com.` and `files.acme.com` are the same host, and folding here
 * is what lets someone paste either form out of a zone file and still succeed.
 */
export function normalizeDomain(raw: string): string {
	const trimmed = raw.trim();
	const withoutRootDot = trimmed.endsWith(".") ? trimmed.slice(0, -1) : trimmed;
	return withoutRootDot.toLowerCase();
}

export function validateDomain(raw: string): DomainValidation {
	const domain = normalizeDomain(raw);
	if (domain.length === 0) {
		return {valid: false, error: "empty"};
	}
	if (domain.length > MAX_DOMAIN_LENGTH) {
		return {valid: false, error: "tooLong"};
	}
	if (!HOSTNAME.test(domain)) {
		return {valid: false, error: "invalid"};
	}
	return {valid: true, domain};
}

/** Lifecycle of the mapping, mirroring `CustomDomainStatus` in the api. */
export type CustomDomainStatus = "PENDING" | "VERIFIED" | "ACTIVE" | "FAILED" | "DISABLED";

/**
 * How far the edge has got with the TLS certificate, mirroring `CdnCertificateStatus` in the api — a
 * vocabulary the api keeps deliberately separate from CloudFront's own, so an SDK upgrade cannot change
 * the wire contract. `UNKNOWN` is what an unrecognised provider value degrades to.
 */
export type CdnCertificateStatus =
	| "PENDING_VALIDATION"
	| "ISSUED"
	| "INACTIVE"
	| "EXPIRED"
	| "VALIDATION_TIMED_OUT"
	| "REVOKED"
	| "FAILED"
	| "UNKNOWN";

/**
 * The owner's view of their custom domain — `CustomDomainView` on the wire.
 *
 * <p>Owned here rather than in `AccountDomainApi` so the rules that read it need no transport dependency;
 * the service imports this type and binds it to the generated spec.
 *
 * <p><strong>Every record field is nullable and they are nullable for different reasons.</strong> The two
 * verification fields are null for a claimed brand subdomain, which carries no challenge — the api used to
 * compose the record anyway and told those owners to publish the literal string `null` into their zone
 * (fixed in kitecorp/transferify-api-service#514). The two routing fields are null when the environment
 * has no custom-domain edge configured at all. `certificateStatus` and `edgeDomainActive` are null before
 * a tenant exists OR when the edge could not be read, which is NOT the same as `false`.
 */
export interface CustomDomain {
	/** The registered host, normalized to lowercase. */
	domain: string;
	status: CustomDomainStatus;
	/** TXT record proving ownership. Null for a claimed brand subdomain, which needs no proof. */
	verificationRecordName?: string | null;
	verificationRecordValue?: string | null;
	/** CNAME routing the host to our edge — always the host itself. Null when no edge is configured. */
	routingRecordName?: string | null;
	/** The shared routing endpoint the CNAME must point at. Null when no edge is configured. */
	routingRecordValue?: string | null;
	certificateStatus?: CdnCertificateStatus | null;
	/** Whether the edge reports the host attached and routing. Null when the edge could not be read. */
	edgeDomainActive?: boolean | null;
	/** Why the last provisioning attempt did not succeed, as a stable machine-readable reason. */
	provisioningError?: string | null;
	/** When ownership was last proven; null while pending. */
	verifiedAt?: string | null;
}

/**
 * The one provisioning reason the ui must BEHAVE differently about.
 *
 * <p>It means the operator has not turned the custom-domain edge on in this environment. Nothing is wrong
 * with the customer's domain and there is nothing about their DNS they can fix, so it must not be rendered
 * as their failure — which is also why the api leaves the mapping `VERIFIED` rather than marking it
 * `FAILED`. Every other reason is a real failure the retry affordance exists for, and each arrives as its
 * own key so none of them reads as a generic shrug.
 */
export const EDGE_NOT_CONFIGURED = "EDGE_NOT_CONFIGURED";

/**
 * What the sender is being told, and therefore which controls make sense.
 *
 * <p>Richer than the raw `status` on purpose: the acceptance criterion is that "we are waiting on your
 * DNS" is distinguishable from "this will never finish", and `status` alone cannot say that — a
 * `VERIFIED` mapping is waiting, provisioning, stalled or blocked on us depending on `certificateStatus`
 * and `provisioningError`.
 */
export type CustomDomainStage =
	/** PENDING — the challenge is issued and we have not seen it in DNS yet. */
	| "awaitingVerification"
	/** FAILED with no provisioning reason — we looked for the TXT record and it was absent or wrong. */
	| "verificationFailed"
	/** FAILED with a provisioning reason — ownership is proven but the edge leg did not complete. */
	| "provisioningFailed"
	/** VERIFIED, blocked on OUR infrastructure flag. Never the customer's fault. */
	| "edgeNotConfigured"
	/** VERIFIED, certificate requested — CloudFront needs the CNAME live before it can validate. */
	| "awaitingCertificate"
	/** VERIFIED, certificate will not complete on its own. Waiting longer is the wrong advice. */
	| "certificateStalled"
	/** VERIFIED, certificate issued — the host is being attached. */
	| "finishing"
	/** VERIFIED, nothing readable from the edge yet. */
	| "provisioning"
	/** ACTIVE — serving, and the only status on which the api will build a recipient link. */
	| "serving"
	/** DISABLED — switched off by the owner, an entitlement lapse, or an abuse response. */
	| "disabled";

/** Drives colour only. The words come from {@link CustomDomainBadge} and the stage's own sentence. */
export type CustomDomainTone = "action" | "progress" | "attention" | "success" | "neutral";

/** The short state word beside the domain. Separate from tone so "on us" is not coloured as a problem. */
export type CustomDomainBadge = "actionNeeded" | "inProgress" | "attention" | "live" | "off";

export type CustomDomainRecordKind = "verification" | "routing";

/** One DNS record to publish, already resolved to literal copy-to-clipboard values. */
export interface CustomDomainRecord {
	kind: CustomDomainRecordKind;
	type: "TXT" | "CNAME";
	name: string;
	value: string;
}

/** Everything the editor renders for a registered domain, decided in one place. */
export interface CustomDomainPresentation {
	stage: CustomDomainStage;
	tone: CustomDomainTone;
	badge: CustomDomainBadge;
	/**
	 * Whether "Check DNS" is offered. It is the SINGLE retry affordance by api design: the same call that
	 * proves ownership also provisions the edge and recovers a failed provision, so there is never a second
	 * control to go looking for.
	 */
	canCheckDns: boolean;
	/**
	 * Whether re-issuing the challenge token is offered.
	 *
	 * <p>Only while ownership is still unproven. Rotation invalidates the published TXT value and returns
	 * the mapping to `PENDING`, so offering it on a live domain would be a one-click way to take your own
	 * domain down — and the button sits next to the one that fixes a stuck verification, which is exactly
	 * where a mis-click is most likely.
	 */
	canRotateToken: boolean;
	records: readonly CustomDomainRecord[];
}

function certificateStage(status: CdnCertificateStatus | null | undefined): CustomDomainStage {
	switch (status) {
		case "PENDING_VALIDATION":
			return "awaitingCertificate";
		case "ISSUED":
		case "INACTIVE":
			return "finishing";
		case "EXPIRED":
		case "VALIDATION_TIMED_OUT":
		case "REVOKED":
		case "FAILED":
			return "certificateStalled";
		// Null before a tenant exists or when the edge could not be read, and UNKNOWN when the provider
		// reported a value this version does not know. Both mean "we cannot tell yet", not "broken".
		default:
			return "provisioning";
	}
}

export function customDomainStage(view: CustomDomain): CustomDomainStage {
	switch (view.status) {
		case "ACTIVE":
			return "serving";
		case "DISABLED":
			return "disabled";
		case "PENDING":
			return "awaitingVerification";
		case "FAILED":
			// The api reaches FAILED down two different paths: verification found no matching TXT record
			// (no reason recorded), or provisioning at the edge threw (reason recorded). Telling the owner
			// to re-check their TXT record when the certificate leg failed would send them to the wrong zone.
			return view.provisioningError ? "provisioningFailed" : "verificationFailed";
		case "VERIFIED":
			if (view.provisioningError === EDGE_NOT_CONFIGURED) {
				return "edgeNotConfigured";
			}
			// Defensive: today only EDGE_NOT_CONFIGURED is recorded without also moving the status to
			// FAILED, but a reason on a VERIFIED mapping is still a report of a failed attempt.
			return view.provisioningError ? "provisioningFailed" : certificateStage(view.certificateStatus);
	}
}

const TONES: Record<CustomDomainStage, CustomDomainTone> = {
	awaitingVerification: "action",
	verificationFailed: "attention",
	provisioningFailed: "attention",
	// Neutral, not "attention": the owner cannot act on our edge flag, and colouring it as a problem would
	// put a red state on their settings page for our configuration.
	edgeNotConfigured: "neutral",
	awaitingCertificate: "action",
	certificateStalled: "attention",
	finishing: "progress",
	provisioning: "progress",
	serving: "success",
	disabled: "neutral",
};

const BADGES: Record<CustomDomainStage, CustomDomainBadge> = {
	awaitingVerification: "actionNeeded",
	verificationFailed: "attention",
	provisioningFailed: "attention",
	edgeNotConfigured: "inProgress",
	awaitingCertificate: "actionNeeded",
	certificateStalled: "attention",
	finishing: "inProgress",
	provisioning: "inProgress",
	serving: "live",
	disabled: "off",
};

/**
 * The records to publish, with every absent one dropped rather than rendered.
 *
 * <p>Dropping is the whole point: both verification fields are null for a claimed brand subdomain and both
 * routing fields are null where no edge is configured, so a template that rendered them unconditionally
 * would instruct the owner to publish the word "null" into their zone. A record is only offered when BOTH
 * halves are present — a name with no value is not an instruction anyone can follow.
 */
export function dnsRecords(view: CustomDomain, stage: CustomDomainStage): readonly CustomDomainRecord[] {
	if (stage === "disabled") {
		return [];
	}
	const records: CustomDomainRecord[] = [];
	// The challenge is spent once ownership is proven, and leaving it on screen invites the owner to think
	// something is still outstanding. The CNAME stays visible for the life of the domain: deleting it is
	// what silently stops the domain serving, so it must never look like a step that is over.
	if (stage === "awaitingVerification" || stage === "verificationFailed") {
		pushRecord(records, "verification", "TXT", view.verificationRecordName, view.verificationRecordValue);
	}
	pushRecord(records, "routing", "CNAME", view.routingRecordName, view.routingRecordValue);
	return records;
}

function pushRecord(
	into: CustomDomainRecord[],
	kind: CustomDomainRecordKind,
	type: "TXT" | "CNAME",
	name: string | null | undefined,
	value: string | null | undefined,
): void {
	if (typeof name === "string" && name.length > 0 && typeof value === "string" && value.length > 0) {
		into.push({kind, type, name, value});
	}
}

export function presentCustomDomain(
	view: CustomDomain,
	{entitled}: {entitled: boolean},
): CustomDomainPresentation {
	const stage = customDomainStage(view);
	if (!entitled) {
		// A lapsed owner's host does not serve whatever the mapping says, and the api would refuse every
		// setup call they could make — only removal stays open to them. Reporting the mapping's own stage
		// would badge the domain "Action needed" and hand them DNS records to publish for a host their
		// plan will not serve, which is an instruction that cannot succeed.
		return {stage, tone: "neutral", badge: "off", canCheckDns: false, canRotateToken: false, records: []};
	}
	return {
		stage,
		tone: TONES[stage],
		badge: BADGES[stage],
		// Live and switched-off domains have nothing to re-check; every other stage is either waiting on
		// DNS or recoverable, and this one call covers both.
		canCheckDns: stage !== "serving" && stage !== "disabled",
		canRotateToken: stage === "awaitingVerification" || stage === "verificationFailed",
		records: dnsRecords(view, stage),
	};
}

/**
 * The section's mutually exclusive states, so the component never re-derives them from three booleans.
 *
 * <p>`lapsed` exists because the api leaves BOTH the read and the removal ungated on every tier — only
 * registering and verifying take the entitlement — so a downgraded owner must still be able to see the
 * domain they hold and take it back. Collapsing that into `locked` is precisely the defect ui#534 reported
 * against the sibling claim editor: the ui contradicted an api that had always allowed it, and the host
 * stayed registered against every other account with nobody able to free it.
 */
export type CustomDomainSectionState = "loading" | "locked" | "lapsed" | "empty" | "managed";

export function customDomainSectionState(
	{entitled, loading, hasDomain}: {entitled: boolean; loading: boolean; hasDomain: boolean},
): CustomDomainSectionState {
	// Loading wins over the upsell for every tier, because a non-entitled sender IS fetched for. Showing
	// the upsell during the load and swapping it afterwards would flash "you have nothing" at someone who
	// is about to be shown the domain they own.
	if (loading) {
		return "loading";
	}
	if (!entitled) {
		return hasDomain ? "lapsed" : "locked";
	}
	return hasDomain ? "managed" : "empty";
}
